import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import moment from 'moment';

// MUI
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import { useTheme, useMediaQuery } from '@mui/material';

// Custom
import PublicPage from '@/ui/PublicPage/PublicPage';
import { FormButton } from '@/ui/Form';
import { helpers } from '@/lib/helpers';
import { DCFile, DCRecord } from '@/@types/lib/dataController';
import api from '@/lib/api';
import LoaderContext from '@/components/LoaderContext/LoaderContext';

import FormButtonShareExcursion from '../Izleti/Buttons/FormButtonShareExcursion';
import FormButtonApplyForExcursion from '../Izleti/Buttons/FormButtonApplyForExcursion';
import gsService from '@/services/gsService';
import { generatePublicIzletDocument } from '@/components/PdfDocuments/IzletDocument';

import PublicIzletiInfoSection from './PublicIzletiInfoSection';
import PublicIzletDaySection from './PublicIzletDaySection';

const { getDayTokenForNumOfDays } = helpers;

const mapLayers = [
  'HGSS:OSM',
  'HGSS:zp_np',
  'HGSS:staze_ferrate',
  'HGSS:msp',
  'HGSS:staze_plan',
  'HGSS:zp_pp',
  'HGSS:zp_sr',
  'HGSS:staze_ostale',
  'HGSS:drzavna_granica',
  'HGSS:staze_ldt',
  'HGSS:staze_ldt_via_adriatica',
  'HGSS:staze_ldt_cldt',
  'HGSS:staze_ldt_via_dinarica',
  'HGSS:izlet_routes',
];


const PublicIzletDetailPage = () => {
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile: boolean = !useMediaQuery(theme.breakpoints.up('lg'));

  const loaderContext = useContext(LoaderContext);

  const { id } = useParams();
  const apiInstance = new api();

  const activeTripsBGColor = '#E5EEF4';

  const [izletDetailRecord, setIzletDetailRecord] = useState<DCRecord | undefined>();

  const firstSectionRecords = useMemo(() => {
    if (izletDetailRecord) {
      return [
        {
          title: 'public.subheadings.datum_polaska',
          content: moment(izletDetailRecord.prvi_dan as string).format('dddd, D.M.YYYY.'),
        },
        {
          title: 'public.subheadings.trajanje',
          content:
            (izletDetailRecord.trajanje_dani as number) + ' ' + t(getDayTokenForNumOfDays(izletDetailRecord.trajanje_dani as number)),
        },
        izletDetailRecord.organizator
          ? { title: 'public.subheadings.organizator', content: izletDetailRecord.organizator as string }
          : undefined,
        izletDetailRecord.vodici
          ? {
            title: 'public.subheadings.vodici',
            content: (
              <Box display='flex' flexDirection='column'>
                {izletDetailRecord.vodici
                  ? (izletDetailRecord.vodici as string[]).map((vodic, index) => (
                    <Typography key={'vodic-typograhpy-' + index} variant="body1">
                      {vodic}
                    </Typography>
                  ))
                  : null}
              </Box>
            ),
          }
          : undefined,
        { title: 'public.subheadings.zahtjevnost', content: izletDetailRecord.zahtjevnost as string, width: 2 },
      ].filter(Boolean);
    } else return [];
  }, [izletDetailRecord]);

  const handlePrint = () => {
    if (izletDetailRecord?.id) {
      generatePublicIzletDocument(izletDetailRecord.id as string);
    }
  };

  const fetchIzleti = () => {
    loaderContext.toggleLoading(true);
    apiInstance
      ._get(`${process.env.REACT_APP_APIPATH}public/izleti/` + id)
      .then((resp) => {
        if (resp.success) {
          if (resp.data) {
            setIzletDetailRecord({
              ...(resp.data as DCRecord),
              gsImgUrl: gsService.getImageUrl(
                mapLayers,
                id as string,
                (resp.data as DCRecord).bbox as [number, number, number, number],
                800,
                600
              ),
            });
          } else {
            // should we notify user about no data found?
          }
        } else {
          // TODO: SNACKBAR?
        }
      })
      .catch((resp) => {
        console.log('error');
        // TODO: SNACKBAR?
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  useEffect(() => {
    fetchIzleti();
  }, []);

  const isHpsIzlet = izletDetailRecord && !!izletDetailRecord.organizator;

  return (
    <PublicPage overrideBgImage>
      {izletDetailRecord ? (
        <>
          <Box width='100%'>
            <Container maxWidth='xxl' style={{ padding: 0 }}>
              <Grid container p={isMobile ? 3 : 10}>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Typography color='primary' variant='h4' component='h1'>
                    {t(isHpsIzlet ? 'public.headings.official_excursion' : 'public.headings.private_excursion')}
                  </Typography>
                </Grid>
                <Grid container size={{ xs: 12, lg: 9 }} mt={isMobile ? 2 : 0}>
                  <Grid size={{ xs: 12, lg: 8 }} mt={isMobile ? 2 : 0} mb={4} columnGap='50px'>
                    <Box textAlign='left' mt={isMobile ? 2 : 0} display='flex' flexDirection='column'>
                      <Typography color='primary' variant="h4" fontSize={!isMobile ? '2.125rem' : '1.75rem'}>
                        {izletDetailRecord.odrediste as string}
                      </Typography>
                      {izletDetailRecord.drzava_ttoken ? (
                        <Typography variant="body1" fontSize={isMobile ? '1.1rem' : '1.2rem'}>
                          {t(izletDetailRecord.drzava_ttoken as string)}
                        </Typography>
                      ) : null}
                      <Box mt={3} display="flex" flexDirection="row" gap={1}>
                        <FormButtonShareExcursion id={id} />
                        <FormButton
                          fullWidth={false}
                          variant="contained"
                          onClick={handlePrint}
                          sx={
                            {
                              /* this removes margin hardcoded into FormButton */
                            }
                          }
                        >
                          {t('buttons.print_excursion')}
                        </FormButton>
                        {izletDetailRecord.prijava ? <FormButtonApplyForExcursion prijava={izletDetailRecord.prijava as string} /> : null}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid pl={isMobile ? 10 : 0} size={{ xs: 12, lg: 4 }}>
                    <Box width='100%' display='flex' justifyContent='end' gap='16px'>
                      {izletDetailRecord.organizator_logotip_url ? (
                        <img
                          style={{ maxHeight: '110px', mixBlendMode: 'darken' }}
                          src={izletDetailRecord.organizator_logotip_url as string}
                        ></img>
                      ) : null}
                      {isHpsIzlet ? <img style={{ maxHeight: '150px' }} src="/images/hps_logo.png"></img> : null}
                    </Box>
                  </Grid>
                  <Grid container size={12} spacing={2}>
                    <Grid container size={12} spacing={2}>
                      {firstSectionRecords.map((entry, index) => entry ?
                        <PublicIzletiInfoSection
                          key={'first-infosection-item-' + index}
                          title={entry.title}
                          content={entry.content}
                          width={entry.width} />
                        : null
                      )}
                    </Grid>
                    <Grid size={12}>
                      <Divider sx={{ borderColor: 'primary.main' }} />
                    </Grid>
                    <Grid container size={12} spacing={3}>
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <Typography variant="body1" fontSize={isMobile ? '1.1rem' : '1.2rem'}>
                          {izletDetailRecord.opis as string}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <img
                          style={{ aspectRatio: '4/3', width: '100%' }}
                          src={
                            (izletDetailRecord.image as DCFile).content
                              ? `data:image/jpeg;base64,${(izletDetailRecord.image as DCFile).content}`
                              : (izletDetailRecord.gsImgUrl as string)
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box width='100%' bgcolor={activeTripsBGColor}>
            <Container maxWidth='xxl' style={{ padding: 0 }}>
              <Grid container width='100%' p={isMobile ? 3 : 10} py={isMobile ? 3 : 5}>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Typography color='primary' variant='h4' component='h1'>
                    {t('public.headings.excursion_plan')}
                  </Typography>
                </Grid>
                <Grid container size={{ xs: 12, lg: 9 }} mt={isMobile ? undefined : '-18px'}>
                  {izletDetailRecord.plan_izleta
                    ? (izletDetailRecord.plan_izleta as DCRecord[]).map((rec, i, arr) =>
                      <PublicIzletDaySection
                        key={'izlet-day-subsection-' + i}
                        danRbr={rec.dan as number}
                        datum={rec.datum as string}
                        opis={rec.opis as string}
                        routePoints={rec.route_points as DCRecord[]}
                        expandedDefault={i === 0} // open first by default
                        noDivider={i === arr.length - 1} // no divider after last sectionw
                      />
                    )
                    : null}
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box width={'100%'}>
            <Container maxWidth={'xxl'} style={{ padding: 0 }}>
              <Grid container size={12} width={'100%'} p={isMobile ? 3 : 10} py={isMobile ? 3 : 5}>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Typography sx={{ maxWidth: { xs: undefined, lg: '375px' } }} color={'primary'} variant={'h4'} component={'h1'}>
                    {t('public.headings.other_info')}
                  </Typography>
                </Grid>
                <Grid container size={{ xs: 12, lg: 9 }} spacing={2} pt={isMobile ? 2 : undefined}>
                  <Grid container size={12} spacing={2}>
                    {izletDetailRecord.ostali_opisi
                      ? (izletDetailRecord.ostali_opisi as DCRecord[]).map((entry, index) => {
                        return (
                          <PublicIzletiInfoSection
                            key={'second-infosection-item-' + index}
                            title={entry.naziv_ttoken as string}
                            content={entry.sadrzaj as string}
                          />
                        );
                      })
                      : null}
                  </Grid>
                  <Grid size={12}>
                    <Divider sx={{ borderColor: 'primary.main' }} />
                  </Grid>
                  <Grid container size={12} spacing={3}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Typography variant="body1" fontSize={isMobile ? '1.1rem' : '1.2rem'}>
                        {izletDetailRecord.dodatne_informacije as string}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid size={12}>
                    <Box>
                      {izletDetailRecord.prijava ? <FormButtonApplyForExcursion prijava={izletDetailRecord.prijava as string} /> : null}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </Box>
        </>
      ) : !izletDetailRecord && !loaderContext.loading ? (
        <Box m={3} display='flex' justifyContent='center'>
          <Typography variant="h5" color='primary'>
            {t('notifications.unexpected_error')}
          </Typography>
        </Box>
      ) : null}
    </PublicPage>
  );
};

export default PublicIzletDetailPage;
